<template>
  <div class="policy-chart-wrapper">
    <div ref="chartDom" class="chart-container" v-show="hasData"></div>
    <div v-if="!hasData" class="empty-state">
      <div class="empty-text">暂无电价信息</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({
      timeSegments: [],
      timeAxis: [],
      priceMap: {}
    })
  }
})

const chartDom = ref(null)
let chartObj = null

// 判断是否有数据
const hasData = computed(() => {
  const timeSegments = props.data?.timeSegments
  return Array.isArray(timeSegments) && timeSegments.length > 0
})

const createChartInstance = () => {
  try {
    if (chartObj) {
      chartObj.dispose()
      chartObj = null
    }
  } catch (e) {
    console.warn('dispose chart error', e)
  }

  if (chartDom.value) {
    chartObj = echarts.init(chartDom.value)
    window.removeEventListener('resize', handleResize)
    window.addEventListener('resize', handleResize)
  }
}

const initChart = () => {
  if (!chartDom.value) return
  if (!chartObj) createChartInstance()

  const { timeSegments } = props.data || {}

  // 如果没有数据，不渲染图表
  if (!timeSegments || !Array.isArray(timeSegments) || timeSegments.length === 0) {
    if (chartObj) {
      chartObj.clear()
    }
    return
  }

  // 按开始时间排序时间段
  const sortedSegments = Array.isArray(timeSegments) ? [...timeSegments] : []
  sortedSegments.forEach(seg => {
    seg.startMinutes = Number(seg.startMinutes) || 0
    seg.endMinutes = Number(seg.endMinutes) || 0
    seg.price = Number(seg.price) || 0
  })
  sortedSegments.sort((a, b) => a.startMinutes - b.startMinutes)

  // 生成X轴数据：00:00到24:00，每小时一个点（共25个点，包括24:00）
  const xAxisData = []
  for (let hour = 0; hour <= 24; hour++) {
    xAxisData.push(`${hour.toString().padStart(2, '0')}:00`)
  }

  // 为每个时间段创建独立的series，确保每个时间段有正确的颜色
  const series = sortedSegments.map((segment) => {
    // 计算时间段在X轴上的起始和结束位置
    const startIndex = Math.floor((Number(segment.startMinutes) || 0) / 60)
    const endMinutes = segment.endMinutes === 1440 ? 1440 : (Number(segment.endMinutes) || 0)
    // 确保24:00包含在内，endIndex应该是25（对应24:00）
    let endIndex = Math.ceil(endMinutes / 60)
    if (endMinutes === 1440) {
      endIndex = 25 // 24:00对应索引25
    }

    // 生成这个时间段的数据（只在对应的时间位置显示）
    const data = new Array(25).fill(null).map((_, index) => {
      // 判断当前索引是否在这个时间段内
      if (index >= startIndex && index < endIndex) {
        return {
          value: segment.price || 0,
          periodName: segment.periodName,
          timeRange: `${segment.startTime || ''}-${segment.endTime || ''}`,
          price: segment.price || 0
        }
      }
      return null
    })

    return {
      name: segment.periodName,
      type: 'line',
      data: data,
      step: 'start', // 使用阶梯折线
      connectNulls: false,
      smooth: false,
      symbol: 'none',
      lineStyle: {
        width: 0 // 不显示线条
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: segment.color },
            { offset: 1, color: segment.colorLight }
          ]
        }
      },
      itemStyle: {
        borderWidth: 0,
        borderColor: 'transparent',
        shadowBlur: 0,
        shadowColor: 'transparent'
      },
      emphasis: {
        itemStyle: {
          borderWidth: 0,
          borderColor: 'transparent',
          shadowBlur: 0,
          shadowColor: 'transparent'
        }
      },
      z: 1
    }
  })

  // 生成用于计算Y轴的数据（包含24:00）
  const lineData = new Array(25).fill(null).map((_, hourIndex) => {
    const hourMinutes = hourIndex * 60
    // 24:00特殊处理
    if (hourIndex === 24) {
      const segment = sortedSegments.find(seg => seg.endMinutes === 1440 && hourMinutes >= seg.startMinutes)
      return segment ? (segment.price || null) : null
    }
    const segment = sortedSegments.find(seg => hourMinutes >= seg.startMinutes && hourMinutes < seg.endMinutes)
    return segment ? (segment.price || null) : null
  })

  // 计算Y轴最大值（基于实际电价数据，动态计算）
  const prices = lineData.filter(v => v !== null && v > 0)

  let yAxisMax = 1000 // 默认值
  let yAxisInterval = 200 // 默认间隔

  if (prices.length > 0) {
    const maxPrice = Math.max(...prices)
    // 动态计算Y轴最大值，向上取整到合适的值
    if (maxPrice > 0) {
      // 根据最大值动态计算合适的间隔和最大值
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxPrice)))
      const normalized = maxPrice / magnitude

      let interval
      if (normalized <= 1) {
        interval = magnitude * 0.2
      } else if (normalized <= 2) {
        interval = magnitude * 0.5
      } else if (normalized <= 5) {
        interval = magnitude
      } else {
        interval = magnitude * 2
      }

      yAxisInterval = interval
      yAxisMax = Math.ceil(maxPrice / interval) * interval

      // 确保至少有5个刻度
      if (yAxisMax / interval < 5) {
        yAxisMax = interval * 5
      }
    }
  }

  // 生成图形元素，在每个时段上方显示名称（仅在有正电价时显示），并且只为 price>0 的时段生成文本
  const graphic = (prices && prices.length > 0)
    ? sortedSegments
      .filter(seg => Number(seg.price) > 0)
      .map((segment) => {
        // 计算时间段的中点位置（基于X轴索引）
        const startIndex = Math.floor((Number(segment.startMinutes) || 0) / 60)
        const endMinutes = segment.endMinutes === 1440 ? 1440 : (Number(segment.endMinutes) || 0)
        let endIndex = Math.ceil(endMinutes / 60)
        if (endMinutes === 1440) {
          endIndex = 25 // 24:00对应索引25
        }
        const centerIndex = (startIndex + endIndex) / 2

        // 计算X轴位置（基于grid的left和right边距）
        const gridLeftPercent = 3
        const gridRightPercent = 10
        const availableWidth = 100 - gridLeftPercent - gridRightPercent
        // X轴有25个点（0-24），中心点在其中的位置
        const xPercent = gridLeftPercent + (centerIndex / 24) * availableWidth

        // 计算Y轴位置：显示在折线正上方
        const gridTopPercent = 15
        const gridBottomPercent = 10
        const availableHeight = 100 - gridTopPercent - gridBottomPercent
        const priceRatio = (segment.price > 0 && yAxisMax > 0) ? (segment.price / yAxisMax) : 0.1
        const pricePercent = priceRatio * availableHeight
        const yPercent = gridTopPercent + (availableHeight - pricePercent) - 8

        return {
          type: 'text',
          left: xPercent + '%',
          top: yPercent + '%',
          style: {
            text: segment.periodName,
            fontSize: 12,
            fontFamily: 'HarmonyOS Sans SC',
            fontWeight: 400,
            fill: '#2E2E2E',
            textAlign: 'center',
            textVerticalAlign: 'middle'
          },
          z: 100
        }
      })
    : []

  const colorMap = {}
  sortedSegments.forEach(seg => {
    colorMap[seg.periodName] = seg.color || '#3A68FF'
  })

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.90)',
      borderColor: 'transparent',
      borderRadius: 16,
      extraCssText: 'backdrop-filter: blur(4px); box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);',
      textStyle: {
        color: '#2E2E2E',
        fontFamily: "HarmonyOS Sans SC",
        fontSize: 14
      },
      axisPointer: {
        type: 'line'
      },
      formatter: (params) => {
        if (!params || params.length === 0) return ''
        const param = params.find(p => p.data && p.data.value !== null && p.data.value !== 0) || params[0]
        const dataItem = param.data

        if (!dataItem || dataItem === null) return ''

        const value = typeof dataItem === 'object' ? dataItem.value : dataItem
        if (value === null || value === 0) return ''

        const periodName = (typeof dataItem === 'object' && dataItem.periodName) || param.seriesName || '未知时段'
        const price = (typeof dataItem === 'object' && dataItem.price) || value || 0
        let dotColor = '#3A68FF'
        if (periodName && colorMap[periodName]) {
          dotColor = colorMap[periodName]
        } else if (param && param.color) {
          dotColor = (typeof param.color === 'string') ? param.color : '#3A68FF'
        }

        return `<div style="color: rgba(46, 46, 46, 0.70); font-family: 'HarmonyOS Sans SC'; font-size: 12px; margin-bottom: 4px;">${params[0].axisValue}</div>
                 <div style="display: flex; align-items: center; gap: 4px; color: #2E2E2E; font-family: 'HarmonyOS Sans SC'; font-size: 14px;">
                  <span style="display:inline-block;margin-right:4px;border-radius:10px;width:6px;height:6px;background-color:${dotColor};"></span>
                  ${periodName}: ${price.toFixed(5)}元</div>`
      }
    },
    graphic: graphic,
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      top: '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#8b93aa',
        fontSize: 12,
        interval: 2 // 每2小时显示一个标签
      }
    },
    yAxis: {
      type: 'value',
      name: '电价（元/kWh）',
      nameLocation: 'end',
      nameTextStyle: {
        align: 'center',
        color: 'rgba(46, 46, 46, 0.70)',
        fontSize: 12,
        fontFamily: 'HarmonyOS Sans SC',
        padding: [0, 0, 0, 35]
      },
      min: 0,
      max: yAxisMax,
      interval: yAxisInterval,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        lineStyle: {
          color: '#6A727C1A',
          type: 'dashed'
        }
      },
      axisLabel: {
        color: '#8b93aa',
        fontSize: 12,
        formatter: (value) => {
          return value.toFixed(0)
        }
      }
    },
    series: series
  }

  try {
    // 清理并设置
    chartObj.clear()
    chartObj.setOption(option)
  } catch (e) {
    console.error('PolicyChart setOption error', e)
  }
}

const handleResize = () => {
  if (chartObj) {
    chartObj.resize()
  }
}

watch(() => props.data, (newData) => {
  if (hasData.value && chartDom.value) {
    nextTick(() => {
      if (!chartObj && chartDom.value) {
        chartObj = echarts.init(chartDom.value)
        window.addEventListener('resize', handleResize)
      }
      if (chartObj) {
        initChart()
      }
    })
  } else if (chartObj) {
    // 如果没有数据，清空图表
    chartObj.clear()
  }
}, { deep: true, immediate: false })

watch(hasData, (newVal) => {
  if (newVal && chartDom.value) {
    nextTick(() => {
      if (!chartObj && chartDom.value) {
        chartObj = echarts.init(chartDom.value)
        window.addEventListener('resize', handleResize)
      }
      if (chartObj) {
        initChart()
      }
    })
  } else if (chartObj) {
    chartObj.clear()
  }
}, { immediate: false })

onMounted(() => {
  if (hasData.value && chartDom.value) {
    nextTick(() => {
      if (!chartObj && chartDom.value) {
        chartObj = echarts.init(chartDom.value)
        window.addEventListener('resize', handleResize)
      }
      if (chartObj) {
        initChart()
      }
    })
  }
})

onUnmounted(() => {
  if (chartObj) {
    chartObj.dispose()
    chartObj = null
  }
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped lang="scss">
.policy-chart-wrapper {
  width: 100%;
  height: 100%;
  min-height: 300px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 300px;

  // 确保图表渲染时没有间隙
  :deep(canvas) {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }

  :deep(.echarts-bar) {
    shape-rendering: crispEdges;
  }
}

.empty-state {
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;

  .empty-text {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    color: rgba(46, 46, 46, 0.5);
  }
}
</style>
